import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Chip,
  Divider,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";

import FlagIcon from "@mui/icons-material/Flag";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { InfoOutlined } from "@mui/icons-material";
import GlobalButton from "../../shared/utils/GlobalButton";
import axios from "axios";
import Swal from "sweetalert2";
import ResolveInstallation from "./ResolveInstallation";
import { useMultiMediaUpload } from "../../helperFunctions/Usemultimediaupload.js";
import MediaPreview from "../../helperFunctions/MediaPreview.js";
const serverURL = process.env.REACT_APP_URL;

const MAX_EVIDENCE_PHOTOS = 4;
const MAX_EVIDENCE_PHOTO_MB = 2; // assumption — confirm against your upload limits
const MAX_VIDEO_SIZE_MB = 10; // assumption — confirm against your upload limits

const InstallationFlag = ({
  data,
  flagStatus = "",
  onFlagSuccess,
  onFlagResolve,
}) => {
  const productId = data?.productId;
  const flaggedBy = localStorage.getItem("userId");
  const [flagModal, setFlagModal] = useState(false);
  const [resolvedFlagModal, setResolvedFlagModal] = useState(false);
  const [flagData, setFlagData] = useState(null);

  const [error, setError] = useState("");

  const [flagReason, setFlagReason] = useState("");
  const [flagRemark, setFlagRemark] = useState("");
  const [loading, setLoading] = useState(false);

  // --- evidence media: same upload primitives as ResolveInstallation ---
  const [mediaTab, setMediaTab] = useState("photos");
  const [mediaPreview, setMediaPreview] = useState(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const photos = useMultiMediaUpload({
    allowedTypes: ["image/jpeg", "image/png"],
    maxSizeMB: MAX_EVIDENCE_PHOTO_MB,
    maxCount: MAX_EVIDENCE_PHOTOS,
    mode: "multiple",
  });
  const video = useMultiMediaUpload({
    allowedTypePrefix: "video/",
    maxSizeMB: MAX_VIDEO_SIZE_MB,
    mode: "single",
  });

  const videoItem = video?.items?.[0];

  const resetEvidence = () => {
    photos.reset();
    video.reset();
    setMediaTab("photos");
  };

  const handleOpenFlag = () => {
    setFlagReason(flagReason || "");
    setFlagRemark(flagRemark || "");
    setError("");
    setFlagModal(true);
  };
  const handleOpenResolvedFlag = async () => {
    try {
      setError("");
      setLoading(true);

      const response = await axios.get(
        `${serverURL}/installation-flags/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const responseData = response?.data?.data;

      setFlagData(responseData);
      setResolvedFlagModal(true);
    } catch (error) {
      console.error("Get installation flag error:", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while getting installation flag",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFlag = () => {
    setFlagModal(false);
    setError("");
    resetEvidence();
  };

  const handleCloseResolvedFlag = () => {
    setResolvedFlagModal(false);
    setError("");
  };

  const handleSubmitFlag = async () => {
    if (!flagReason) {
      setError("Please select a Issue");
      return;
    }

    if (flagReason === "Others" && !flagRemark.trim()) {
      setError("Please enter a Description");
      return;
    }

    if (
      photos.items?.some((item) => item?.uploading) ||
      video.items?.some((item) => item?.uploading)
    ) {
      setError("Please wait for the evidence to finish uploading.");
      return;
    }

    try {
      setError("");
      setLoading(true);

      const payload = {
        productId: productId,
        flagIssue: flagReason,
        description: flagRemark.trim(),
        flaggedBy: flaggedBy,
        evidenceImages: photos.items.map((item) => item.url).filter(Boolean),
        evidenceVideo: videoItem?.url || null,
      };

      const response = await axios.post(
        `${serverURL}/installation-flags/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const status = response?.data?.data?.flag?.status;

      onFlagSuccess?.(status);

      setFlagModal(false);

      setFlagReason("");
      setFlagRemark("");
      resetEvidence();

      Swal.fire({
        icon: "success",
        title: "Installation Flagged",
        text: response?.data?.message,
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Flag installation error:", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while flagging installation",
      );
    } finally {
      setLoading(false);
    }
  };

  const displayError = error || photos.error || video.error;

  return (
    <>
      {flagStatus === "" ? (
        <GlobalButton
          variant="primary"
          size="medium"
          onClick={handleOpenFlag}
          leftIcon={<FlagIcon />}
          disabled={!data?.installationJob?.imagesOfInstallation?.frontPhoto}
        >
          Flag Installation
        </GlobalButton>
      ) : flagStatus === "Flagged" ? (
        <div className="flex items-center">
          <img
            src="/assests/AdminDashboard/flag_filled.png "
            width={40}
            height={40}
            alt="flag"
            className="mx-2"
          />
          <GlobalButton
            variant="secondary"
            size="medium"
            onClick={handleOpenResolvedFlag}
          >
            View & Resolved
          </GlobalButton>
        </div>
      ) : flagStatus === "Resolved" ? (
        <div className="flex items-center">
          <img
            src="/assests/AdminDashboard/flag_green.png "
            width={40}
            height={40}
            alt="flag"
            className="mx-2"
          />
          <GlobalButton
            variant="secondary"
            onClick={handleOpenResolvedFlag}
            size="medium"
          >
            View Details
          </GlobalButton>
        </div>
      ) : null}

      <Dialog
        open={flagModal}
        onClose={handleCloseFlag}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            padding: "24px",
            gap: "24px",
          },
        }}
      >
        <div className="flex justify-between items-center ">
          <h2 className="font-bold text-primary ">Flag Installation</h2>
          <IconButton onClick={handleCloseFlag}>
            <CloseIcon />
          </IconButton>
        </div>

        <div>
          <Box
            sx={{
              border: "1px solid #d9d9d9",
              borderRadius: "7px",
              p: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 166,
                  height: 116,
                  flexShrink: 0,
                  borderRadius: "4px",
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <img
                  src={data?.installationJob?.imagesOfInstallation?.frontPhoto}
                  alt="Installation"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <h4 className="font-semibold text-primary">
                  {data?.product?.productType || ""}
                </h4>

                <div className="flex items-center">
                  <p className="text-tertiary font-small">Vendor :&nbsp; </p>
                  <h6 className="text-anchor font-medium">
                    {data?.product?.vendorId?.vendorBusinessName || ""}
                  </h6>
                </div>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/assests/AdminDashboard/campaign.png"
                    alt="campaign"
                    style={{
                      width: "14px",
                      height: "14px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />

                  <p className="font-medium text-primary">
                    {data?.campaignId?.campaignTitle}
                  </p>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/assests/AdminDashboard/store.png"
                    alt="store"
                    style={{
                      width: "14px",
                      height: "14px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                  <p className="font-medium text-primary">
                    {data?.job?.storeName}
                  </p>
                </Box>
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#253041",
              mb: 0.7,
            }}
          >
            ISSUE
            <span style={{ color: "#e53935" }}>*</span>
          </Typography>

          <TextField
            select
            fullWidth
            value={flagReason}
            onChange={(e) => {
              setFlagReason(e.target.value);
              setError("");
            }}
            SelectProps={{
              displayEmpty: true,
            }}
            sx={{
              mb: 1.8,
              "& .MuiOutlinedInput-root": {
                height: 48,
                borderRadius: "5px",
                fontSize: "13px",
              },
            }}
          >
            <MenuItem value="" disabled>
              <span style={{ color: "#999" }}>Select Installation Issue</span>
            </MenuItem>

            <MenuItem value="Incorrect Installation">
              Incorrect Installation
            </MenuItem>

            <MenuItem value="Poor Finishing">Poor Finishing</MenuItem>

            <MenuItem value="Incorrect Positioning">
              Incorrect Positioning
            </MenuItem>

            <MenuItem value="Damaged Element">Damaged Element</MenuItem>

            <MenuItem value="Missing Element">Missing Element</MenuItem>

            <MenuItem value="Quality Issue">Quality Issue</MenuItem>
            <MenuItem value="Measurement/Size Issue">
              Measurement/Size Issue
            </MenuItem>
            <MenuItem value="Branding/Artwork Issue">
              Branding/Artwork Issue
            </MenuItem>
            <MenuItem value="Others">Others</MenuItem>
          </TextField>

          {/* --- evidence media: Photos / Video tabs --- */}
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#253041",
              mb: 0.7,
            }}
          >
            EVIDENCE
          </Typography>

          <Box
            sx={{ border: "1px solid #d9d9d9", borderRadius: "5px", mb: 1.8 }}
          >
            <Tabs
              value={mediaTab}
              onChange={(_, val) => setMediaTab(val)}
              sx={{
                minHeight: 38,
                borderBottom: "1px solid #eee",
                "& .MuiTab-root": {
                  minHeight: 38,
                  fontSize: "12.5px",
                  fontWeight: 500,
                  textTransform: "none",
                },
              }}
            >
              <Tab value="photos" label={`Photos (${photos.items.length})`} />
              <Tab value="video" label={`Video (${videoItem?.file ? 1 : 0})`} />
            </Tabs>

            <Box sx={{ p: 1.2 }}>
              {mediaTab === "photos" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1,
                  }}
                >
                  {photos?.items?.map((item, index) => (
                    <Box
                      key={item.preview}
                      onClick={() =>
                        !item?.uploading &&
                        setMediaPreview({
                          type: "image",
                          src: item?.url || item?.preview,
                        })
                      }
                      sx={{
                        position: "relative",
                        height: 64,
                        borderRadius: "6px",
                        overflow: "hidden",
                        backgroundColor: "#f5f5f5",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={item.preview}
                        alt={`Evidence ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {item.uploading && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "10px",
                          }}
                        >
                          Uploading...
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => photos.removeAt(item.preview)}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          padding: "2px",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
                      </IconButton>
                    </Box>
                  ))}

                  {photos?.items?.length < MAX_EVIDENCE_PHOTOS && (
                    <Box
                      onClick={() => photoInputRef.current?.click()}
                      sx={{
                        height: 64,
                        borderRadius: "6px",
                        border: "1.5px dashed #c7cbd1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "#9aa0a6",
                        "&:hover": { borderColor: "#9aa0a6" },
                      }}
                    >
                      <AddPhotoAlternateIcon sx={{ fontSize: 20 }} />
                    </Box>
                  )}

                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                      photos.addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </Box>
              )}

              {mediaTab === "video" && (
                <Box>
                  {videoItem?.preview ? (
                    <Box
                      onClick={() =>
                        !videoItem?.uploading &&
                        setMediaPreview({
                          type: "video",
                          src: videoItem?.url || videoItem?.preview,
                        })
                      }
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 72,
                        borderRadius: "6px",
                        overflow: "hidden",
                        backgroundColor: "#000",
                        cursor: "pointer",
                      }}
                    >
                      <video
                        src={videoItem?.preview}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <PlayCircleIcon
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#fff",
                          fontSize: 26,
                        }}
                      />
                      {videoItem?.uploading && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "10px",
                          }}
                        >
                          Uploading...
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => video.reset()}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          backgroundColor: "rgba(0,0,0,0.55)",
                          padding: "2px",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => videoInputRef.current?.click()}
                      sx={{
                        width: 120,
                        height: 72,
                        borderRadius: "6px",
                        border: "1.5px dashed #c7cbd1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.3,
                        cursor: "pointer",
                        color: "#9aa0a6",
                        "&:hover": { borderColor: "#9aa0a6" },
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: 20 }} />
                      <Typography sx={{ fontSize: "10px" }}>
                        Max 1 video
                      </Typography>
                    </Box>
                  )}

                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={(e) => {
                      video.addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#253041",
              mb: 0.7,
            }}
          >
            DESCRIPTION
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            value={flagRemark}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setFlagRemark(e.target.value);
                setError("");
              }
            }}
            placeholder="Describe Installation Issue..."
            inputProps={{
              maxLength: 100,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "5px",
                fontSize: "13px",
                alignItems: "flex-start",
              },

              "& textarea": {
                minHeight: "22px !important",
              },

              "& textarea::placeholder": {
                color: "#aaa",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 0.3,
              mb: 1.8,
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: "#999",
              }}
            >
              {flagRemark.length}/100
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.2,
              py: 0.9,
              borderRadius: "15px",
              backgroundColor: "#fdecce",
              border: "1px solid #fdecce",
              mb: 1.8,
            }}
          >
            <InfoOutlined
              sx={{
                fontSize: 15,
                color: "#333",
              }}
            />

            <Typography
              sx={{
                fontSize: "12px",
                color: "#000000",
                fontWeight: "400",
                lineHeight: 1.4,
              }}
            >
              On submission the installation flag, vendor will be notified
              automatically via Whatsapp
            </Typography>
          </Box>

          {displayError && <p className="text-error">{displayError}</p>}
        </div>

        <div>
          <GlobalButton onClick={handleSubmitFlag} disabled={loading}>
            {loading ? "Submitting..." : "Submit Flag"}
          </GlobalButton>
        </div>
      </Dialog>

      <Dialog open={resolvedFlagModal} maxWidth="md" fullWidth>
        <ResolveInstallation
          data={flagData}
          onClose={handleCloseResolvedFlag}
          onFlagResolve={onFlagResolve}
          setLoading={setLoading}
          loading={loading}
        />
      </Dialog>

      <MediaPreview
        open={!!mediaPreview}
        onClose={() => setMediaPreview(null)}
        type={mediaPreview?.type}
        src={mediaPreview?.src}
      />
    </>
  );
};

export default InstallationFlag;
